import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Handshake,
  Share2,
  Wallet,
  Crown,
  CheckCircle2,
  XCircle,
  Info,
  Calendar,
  CreditCard,
  MousePointerClick,
  LineChart,
  Flag,
  AlertTriangle,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';
import AffiliateApplyForm from '../components/AffiliateApplyForm';

/* ─────────── DATA ─────────── */

const HEADLINE_STATS = [
  { big: '40%', label: 'Commission on every new member' },
  { big: '20%', label: 'Recurring monthly, for as long as they stay' },
  { big: '$10', label: 'Monthly membership price' },
  { big: '$100', label: 'Annual membership price' },
];

const AUDIENCE = [
  'Content creators on TikTok, Instagram, YouTube or podcasts',
  'Coaches, trainers or educators with an engaged audience',
  'Anyone in personal development, mindset, discipline or productivity',
  'Existing Levanta members who want to earn by sharing what they use',
];

const STEPS = [
  {
    Icon: Handshake,
    number: '01',
    title: 'Apply & get your link',
    body:
      'Fill out a short application. Once approved, you get a unique referral link and promo code that tracks every signup to your account.',
  },
  {
    Icon: Share2,
    number: '02',
    title: 'Share with your audience',
    body:
      'Drop the link in videos, posts, stories, podcast descriptions, DMs or a simple verbal mention. No scripts. Just honest recommendation.',
  },
  {
    Icon: Wallet,
    number: '03',
    title: 'Earn every month',
    body:
      'You get paid for the first month and every month your referred members stay active. The longer they grow with Levanta, the longer you earn.',
  },
];

/* 12-month earnings example, recomputed from the PDF. */
const EARNINGS_ROWS = [
  { m: 1, newMembers: 20, active: 20, newComm: 80, recComm: 0, total: 80 },
  { m: 2, newMembers: 20, active: 40, newComm: 80, recComm: 40, total: 120 },
  { m: 3, newMembers: 20, active: 60, newComm: 80, recComm: 80, total: 160 },
  { m: 4, newMembers: 20, active: 80, newComm: 80, recComm: 120, total: 200 },
  { m: 5, newMembers: 20, active: 100, newComm: 80, recComm: 160, total: 240 },
  { m: 6, newMembers: 20, active: 120, newComm: 80, recComm: 200, total: 280 },
  { m: 7, newMembers: 20, active: 140, newComm: 80, recComm: 240, total: 320 },
  { m: 8, newMembers: 20, active: 160, newComm: 80, recComm: 280, total: 360 },
  { m: 9, newMembers: 20, active: 180, newComm: 80, recComm: 320, total: 400 },
  { m: 10, newMembers: 20, active: 200, newComm: 80, recComm: 360, total: 440 },
  { m: 11, newMembers: 20, active: 220, newComm: 80, recComm: 400, total: 480 },
  { m: 12, newMembers: 20, active: 240, newComm: 80, recComm: 440, total: 520 },
];

const PAYOUT_RULES = [
  {
    Icon: Wallet,
    title: 'Minimum payout threshold',
    body: '$20 — commissions accumulate until the threshold is reached.',
  },
  {
    Icon: Calendar,
    title: 'Payment schedule',
    body: 'Monthly, on the 1st of each month for the previous month’s earnings.',
  },
  {
    Icon: CreditCard,
    title: 'Payment method',
    body: 'PayPal or bank transfer.',
  },
  {
    Icon: MousePointerClick,
    title: 'Tracking',
    body: 'Your unique referral link and promo code both track to your account.',
  },
  {
    Icon: LineChart,
    title: 'Dashboard',
    body: 'Real-time view of clicks, signups, active members and earnings.',
  },
  {
    Icon: Flag,
    title: 'If a member cancels',
    body:
      'Commission stops from that month forward. Previous months are paid in full and will never be reversed.',
  },
];

const MUST_DO = [
  'Disclose the affiliate relationship in all content (#ad, #partner or #affiliate).',
  'Represent Levanta accurately — share what the platform actually does, not exaggerated claims.',
  'Only promote Levanta to audiences who would genuinely benefit from a structured growth system.',
  'Use your unique referral link or promo code so your referrals are tracked correctly.',
];

const MUST_NOT = [
  'Promise specific income results to your audience on Levanta’s behalf.',
  'Use paid ads to promote your referral link without prior written approval.',
  'Self-refer — creating accounts through your own link is prohibited and results in immediate removal.',
  'Use misleading or false claims about Levanta’s features, price or results.',
];

const RESERVES = [
  'Remove any affiliate from the program for violation of these rules.',
  'Adjust commission rates with 30 days’ written notice to existing affiliates.',
  'Reject any application without providing a reason.',
];

/* ─────────── PAGE ─────────── */

const Affiliates = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const verifyState = params.get('verify'); // 'invalid' | 'expired' | null

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* ───── HERO ───── */}
      <section className="relative pt-[110px] md:pt-[170px] pb-16 md:pb-28 px-6 overflow-hidden min-h-[640px] md:min-h-[820px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="affiliate-eyebrow"
          >
            <Handshake className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Affiliate Partner Program
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
            data-testid="affiliate-hero-title"
          >
            Build passive income by sharing{' '}
            <em className="italic font-light text-blue-600">
              something that actually works.
            </em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Earn 40% on every new member and 20% recurring for as long as they
            stay. Do the work once — create the content, share the link — and
            the income continues every month they keep growing.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="#apply"
              data-testid="affiliate-hero-apply"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Apply to become a partner
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/80 backdrop-blur text-[#0b1f3a] font-semibold ring-1 ring-black/10 hover:bg-white transition-all duration-200"
            >
              How it works
            </a>
          </div>

          <p className="mt-5 text-sm font-medium" data-testid="affiliate-hero-already">
            <span className="text-white drop-shadow-[0_1px_2px_rgba(11,31,58,0.55)]">
              Already an affiliate?
            </span>{' '}
            <Link
              to="/affiliates/login"
              data-testid="affiliate-hero-login"
              className="font-semibold text-blue-700 hover:text-blue-900 underline"
            >
              Log in to your dashboard →
            </Link>
          </p>

          {/* Headline stat row */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
            {HEADLINE_STATS.map((s) => (
              <div
                key={s.label}
                className="bg-white/85 backdrop-blur-sm rounded-2xl ring-1 ring-black/5 shadow-sm px-5 py-5 text-center"
              >
                <p className="text-3xl md:text-4xl font-black text-blue-600 tracking-tight">
                  {s.big}
                </p>
                <p className="mt-1.5 text-[12px] md:text-[13px] text-[#0b1f3a]/70 leading-snug font-medium">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── WHAT IS LEVANTA + WHAT IS THE PROGRAM ───── */}
      <section className="px-6 py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 md:gap-14">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
              What is Levanta?
            </p>
            <h2 className="text-3xl md:text-4xl font-black leading-[1.1] tracking-tight text-[#0b1f3a] mb-6">
              The structured{' '}
              <em className="italic font-light text-blue-600">personal growth system.</em>
            </h2>
            <div className="space-y-4 text-[#0b1f3a]/80 leading-relaxed text-[17px]">
              <p>
                Levanta replaces motivation-dependent self-help with a clear,
                repeatable system built on three foundations: discipline,
                structure and community.
              </p>
              <p>
                Members progress through structured skill paths organised into
                four Worlds — Foundation, Creation, Connection and Fuel — with a
                daily cycle of Learning, Practice, Reflection and Tracking.
              </p>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
              What is the Affiliate Partner Program?
            </p>
            <h2 className="text-3xl md:text-4xl font-black leading-[1.1] tracking-tight text-[#0b1f3a] mb-6">
              Not a one-time bonus.{' '}
              <em className="italic font-light text-blue-600">A passive income stream.</em>
            </h2>
            <div className="space-y-4 text-[#0b1f3a]/80 leading-relaxed text-[17px]">
              <p>
                You earn a commission on every new member who joins through
                your link — and you keep earning every month they stay active.
              </p>
              <p>
                The program grows with you. The more people you bring in, the
                larger your recurring base becomes. One piece of content today
                can pay you next year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── WHO IS THIS FOR ───── */}
      <section className="px-6 py-20 md:py-24 bg-[#f4f8fc]">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Who is this for?
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
            The people we want to partner with{' '}
            <em className="italic font-light text-blue-600">most.</em>
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {AUDIENCE.map((a, i) => (
              <div
                key={a}
                className="flex items-start gap-4 bg-white rounded-2xl p-6 ring-1 ring-black/5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
              >
                <div className="shrink-0 w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <p className="text-[#0b1f3a] text-[17px] leading-snug pt-1.5">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section id="how-it-works" className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            How it works
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Three steps.{' '}
            <em className="italic font-light text-blue-600">Then it compounds.</em>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map(({ Icon, number, title, body }) => (
              <div
                key={number}
                className="relative rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <p className="text-[11px] font-mono tracking-[0.2em] text-blue-600/70 mb-5">
                  STEP {number}
                </p>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white flex items-center justify-center mb-5 shadow-md">
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#0b1f3a] leading-tight mb-3">
                  {title}
                </h3>
                <p className="text-[#0b1f3a]/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── COMMISSION STRUCTURE ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4 text-center">
            Commission structure
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-center mb-14">
            Paid for what you bring —{' '}
            <em className="italic font-light text-blue-300">and what stays.</em>
          </h2>

          {/* Monthly */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-8 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-black">Monthly members</h3>
                <span className="text-sm font-mono text-blue-300">$10/mo</span>
              </div>
              <div className="space-y-4 text-white/80">
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
                  <div>
                    <p className="font-semibold text-white">Month 1 signup</p>
                    <p className="text-sm text-white/60">40% of first month</p>
                  </div>
                  <p className="text-2xl font-black text-blue-300 shrink-0">$4.00</p>
                </div>
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
                  <div>
                    <p className="font-semibold text-white">Every month they stay</p>
                    <p className="text-sm text-white/60">
                      20% of each recurring payment
                    </p>
                  </div>
                  <p className="text-2xl font-black text-blue-300 shrink-0">$2.00</p>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-white">If they cancel</p>
                    <p className="text-sm text-white/60">
                      Commission stops. Previous months never reversed.
                    </p>
                  </div>
                  <p className="text-2xl font-black text-white/40 shrink-0">$0</p>
                </div>
              </div>
            </div>

            {/* Annual */}
            <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-8 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-black">Annual members</h3>
                <span className="text-sm font-mono text-blue-300">$100/yr</span>
              </div>
              <div className="space-y-4 text-white/80">
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
                  <div>
                    <p className="font-semibold text-white">Annual signup</p>
                    <p className="text-sm text-white/60">
                      30% of annual payment — one-time, paid immediately
                    </p>
                  </div>
                  <p className="text-2xl font-black text-blue-300 shrink-0">$30.00</p>
                </div>
                <p className="text-sm text-white/60 pt-2 leading-relaxed">
                  Annuals give a larger up-front commission. Monthlies build
                  recurring income. Smart partners promote both, to different
                  audiences.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex items-start gap-3 bg-white/5 rounded-2xl p-5 ring-1 ring-white/10 max-w-3xl mx-auto">
            <Info className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
            <p className="text-sm text-white/75 leading-relaxed">
              You are paid for every month a member is active. If a member
              cancels, your commission stops from the month of cancellation —
              and you are never charged back for months already paid.
            </p>
          </div>
        </div>
      </section>

      {/* ───── REAL EARNINGS EXAMPLE ───── */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Real earnings example
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-4">
            What 20 new members a month{' '}
            <em className="italic font-light text-blue-600">actually looks like.</em>
          </h2>
          <p className="text-center text-[#0b1f3a]/65 max-w-2xl mx-auto mb-12 leading-relaxed">
            Assumes all members stay active. 20 is just the example — there is
            no cap on how many people you can refer. Bring 40, and the numbers
            double. Bring 100, and the picture changes.
          </p>

          <div className="overflow-x-auto rounded-3xl ring-1 ring-black/5 shadow-sm">
            <table className="w-full min-w-[720px] text-sm md:text-[15px]">
              <thead>
                <tr className="bg-[#f4f8fc] text-[#0b1f3a]">
                  <th className="px-4 py-4 text-left font-semibold">Month</th>
                  <th className="px-4 py-4 text-center font-semibold">New members</th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Total active
                  </th>
                  <th className="px-4 py-4 text-right font-semibold">
                    New (40%)
                  </th>
                  <th className="px-4 py-4 text-right font-semibold">
                    Recurring (20%)
                  </th>
                  <th className="px-4 py-4 text-right font-semibold text-blue-600">
                    This month
                  </th>
                </tr>
              </thead>
              <tbody>
                {EARNINGS_ROWS.map((r) => (
                  <tr
                    key={r.m}
                    className="border-t border-black/5 hover:bg-[#fafcff] transition-colors"
                  >
                    <td className="px-4 py-3.5 font-mono text-[#0b1f3a]/70">
                      Month {r.m}
                    </td>
                    <td className="px-4 py-3.5 text-center text-[#0b1f3a]/80">
                      {r.newMembers}
                    </td>
                    <td className="px-4 py-3.5 text-center text-[#0b1f3a]/80">
                      {r.active}
                    </td>
                    <td className="px-4 py-3.5 text-right text-[#0b1f3a]/80">
                      ${r.newComm}
                    </td>
                    <td className="px-4 py-3.5 text-right text-[#0b1f3a]/80">
                      ${r.recComm}
                    </td>
                    <td className="px-4 py-3.5 text-right font-bold text-blue-600">
                      ${r.total}
                    </td>
                  </tr>
                ))}
                <tr className="bg-[#0b1f3a] text-white">
                  <td className="px-4 py-4 font-black uppercase tracking-wider text-xs">
                    Year total
                  </td>
                  <td className="px-4 py-4 text-center font-semibold">240</td>
                  <td className="px-4 py-4 text-center font-semibold">240</td>
                  <td className="px-4 py-4 text-right font-semibold">$960</td>
                  <td className="px-4 py-4 text-right font-semibold">$2,640</td>
                  <td className="px-4 py-4 text-right font-black text-cyan-300 text-lg">
                    $3,600
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Month 13 passive income moment */}
          <div className="mt-16 rounded-3xl bg-gradient-to-br from-blue-50 via-white to-cyan-50 ring-1 ring-blue-100 p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl" />
            <div className="relative">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
                Month 13 — the passive income moment
              </p>
              <h3 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] mb-6 max-w-3xl">
                You bring in{' '}
                <em className="italic font-light text-blue-600">zero</em> new
                members. You still earn.
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6">
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#0b1f3a]/55 mb-2">
                    Active members
                  </p>
                  <p className="text-3xl font-black text-[#0b1f3a]">240</p>
                </div>
                <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6">
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#0b1f3a]/55 mb-2">
                    Their revenue to Levanta
                  </p>
                  <p className="text-3xl font-black text-[#0b1f3a]">$2,400</p>
                </div>
                <div className="rounded-2xl bg-blue-600 text-white p-6 shadow-lg">
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-200 mb-2">
                    Your recurring commission
                  </p>
                  <p className="text-3xl font-black">$480</p>
                </div>
              </div>
              <p className="mt-8 text-[#0b1f3a]/75 leading-relaxed max-w-3xl text-[17px]">
                That’s what a recurring affiliate program looks like. You did
                the work once — created the content, shared the link, brought
                the members in — and the income continues as long as they stay
                active. Bring 40, it doubles. Bring 100, the numbers change
                your life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FOUNDING AFFILIATE ───── */}
      <section className="px-6 py-24 md:py-28 bg-gradient-to-b from-[#eaf3fb] to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold tracking-[0.18em] uppercase mb-5">
              <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
              Limited to the first 50
            </span>
            <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a]">
              Founding Affiliate —{' '}
              <em className="italic font-light text-blue-600">permanently elevated terms.</em>
            </h2>
            <p className="mt-5 text-[#0b1f3a]/65 max-w-2xl mx-auto leading-relaxed">
              The first 50 partners get higher commissions and a lifetime
              recurring cap. Once we’re past 50, the window closes — existing
              founders keep their terms forever.
            </p>
          </div>

          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-lg overflow-hidden">
            <div className="grid grid-cols-3">
              {/* header */}
              <div className="px-5 py-5 bg-[#f4f8fc]" />
              <div className="px-5 py-5 bg-[#f4f8fc] text-center border-l border-black/5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/60">
                  Standard affiliate
                </p>
              </div>
              <div className="px-5 py-5 bg-gradient-to-br from-blue-600 to-cyan-500 text-center text-white">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] flex items-center justify-center gap-1.5">
                  <Crown className="w-3 h-3" strokeWidth={3} />
                  Founding (first 50)
                </p>
              </div>

              {/* rows */}
              {[
                ['First month commission', '40% ($4)', '50% ($5)'],
                ['Recurring commission', '20% ($2)', '25% ($2.50)'],
                ['Annual commission', '30% ($30)', '35% ($35)'],
                ['Recurring cap', '12 months', 'Lifetime'],
                ['Status badge', '—', 'Founding Partner'],
              ].map(([label, std, founding], i) => (
                <React.Fragment key={label}>
                  <div
                    className={`px-5 py-5 text-[#0b1f3a] font-semibold ${
                      i < 4 ? 'border-t border-black/5' : 'border-t border-black/5'
                    }`}
                  >
                    {label}
                  </div>
                  <div className="px-5 py-5 text-center text-[#0b1f3a]/75 border-l border-t border-black/5">
                    {std}
                  </div>
                  <div className="px-5 py-5 text-center font-bold text-blue-700 bg-blue-50/60 border-t border-blue-100">
                    {founding}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── PAYOUT RULES ───── */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Payout rules
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Clear.{' '}
            <em className="italic font-light text-blue-600">Predictable.</em>{' '}
            On time.
          </h2>
          {/* Desktop: 3-col grid (untouched). Mobile: horizontal swipe
              carousel — same UX pattern as the rest of the site. */}
          <div className="hidden md:grid md:grid-cols-3 gap-5">
            {PAYOUT_RULES.map(({ Icon, title, body }) => (
              <div
                key={title}
                className="rounded-3xl bg-[#f4f8fc] p-7 hover:bg-white hover:ring-1 hover:ring-black/5 hover:shadow-lg transition-all duration-300"
              >
                <Icon className="w-6 h-6 text-blue-600 mb-4" strokeWidth={2} />
                <h3 className="text-lg font-black text-[#0b1f3a] mb-2">
                  {title}
                </h3>
                <p className="text-[#0b1f3a]/70 leading-relaxed text-[15px]">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <div
            className="md:hidden -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3"
            style={{ scrollbarWidth: 'none' }}
            data-testid="affiliate-payout-mobile"
          >
            {PAYOUT_RULES.map(({ Icon, title, body }) => (
              <div
                key={title}
                className="shrink-0 w-[80vw] max-w-[320px] snap-start rounded-3xl bg-[#f4f8fc] p-6"
              >
                <Icon className="w-6 h-6 text-blue-600 mb-3" strokeWidth={2} />
                <h3 className="text-lg font-black text-[#0b1f3a] mb-2">
                  {title}
                </h3>
                <p className="text-[#0b1f3a]/70 leading-relaxed text-[15px]">
                  {body}
                </p>
              </div>
            ))}
          </div>
          <style>{`[data-testid="affiliate-payout-mobile"]::-webkit-scrollbar{display:none;}`}</style>
          <p className="md:hidden mt-3 text-center text-xs text-[#0b1f3a]/45 tracking-wider uppercase">
            Swipe → to explore
          </p>
        </div>
      </section>

      {/* ───── RULES & REQUIREMENTS ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Rules &amp; requirements
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Honest promotion.{' '}
            <em className="italic font-light text-blue-600">Real partnership.</em>
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Must do */}
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <h3 className="text-xl font-black text-[#0b1f3a]">What you must do</h3>
              </div>
              <ul className="space-y-4">
                {MUST_DO.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-[#0b1f3a]/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Must not */}
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
                  <XCircle className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <h3 className="text-xl font-black text-[#0b1f3a]">What you must not do</h3>
              </div>
              <ul className="space-y-4">
                {MUST_NOT.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-[#0b1f3a]/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 rounded-3xl bg-[#0b1f3a] text-white p-8 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-white/10 text-cyan-300 flex items-center justify-center">
                <Info className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <h3 className="text-xl font-black">Levanta reserves the right to</h3>
            </div>
            <ul className="grid md:grid-cols-3 gap-4">
              {RESERVES.map((item) => (
                <li
                  key={item}
                  className="bg-white/5 rounded-2xl ring-1 ring-white/10 p-5 text-white/80 leading-relaxed text-[15px]"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ───── APPLY ───── */}
      <section id="apply" className="px-6 py-24 md:py-32 bg-[#0b1f3a] text-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-cyan-400/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4 text-center">
            Apply in two minutes
          </p>
          <h2 className="text-4xl md:text-6xl font-black leading-[1.02] tracking-tight text-center mb-6">
            Get your code.{' '}
            <em className="italic font-light text-blue-300">Start sharing.</em>
          </h2>
          <p className="text-center text-white/70 max-w-2xl mx-auto mb-10 text-lg">
            Fill the form, verify your email, and your unique 8-character
            referral code lands in your inbox. Anyone who uses it gets 10% off
            Levanta — and you start earning immediately.
          </p>

          {verifyState === 'invalid' && (
            <div
              data-testid="affiliate-verify-invalid"
              className="mb-8 flex items-start gap-3 bg-amber-500/10 ring-1 ring-amber-400/40 text-amber-100 rounded-2xl px-5 py-4 text-sm"
            >
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                That verification link is invalid or has already been used. If
                you already verified, check your inbox for your welcome email
                with your code. Otherwise, re-apply below.
              </span>
            </div>
          )}
          {verifyState === 'expired' && (
            <div
              data-testid="affiliate-verify-expired"
              className="mb-8 flex items-start gap-3 bg-amber-500/10 ring-1 ring-amber-400/40 text-amber-100 rounded-2xl px-5 py-4 text-sm"
            >
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                That verification link has expired (links are valid for 48
                hours). Re-submit the form below and we'll send a fresh one.
              </span>
            </div>
          )}

          <AffiliateApplyForm />

          <p className="text-center text-white/55 text-sm mt-10">
            Not ready yet? Read{' '}
            <Link to="/how-it-works" className="underline hover:text-white">
              how Levanta works
            </Link>{' '}
            or our{' '}
            <Link to="/faq" className="underline hover:text-white">
              FAQ
            </Link>{' '}
            first.
          </p>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default Affiliates;
